import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { friendlyError } from "@/lib/errors";
import { toast } from "@/components/ui/toast";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { fetchSettings, updateSettings, type StoreSettings } from "./storeApi";

/**
 * Las tres pantallas de Configuracion editan la MISMA fila, cada una un grupo
 * de columnas. Compartir el borrador evita el fallo clasico: guardar Identidad
 * pisando lo que alguien acababa de cambiar en Comercial.
 *
 * Cada pantalla manda solo sus columnas en el update, asi que dos personas en
 * secciones distintas no se estorban.
 */
export function useSettingsForm<T extends Record<string, unknown>>(
  desdeAjustes: (ajustes: StoreSettings) => T,
) {
  const { org } = useActiveOrg();
  const qc = useQueryClient();
  const orgId = org?.organizationId ?? null;

  const consulta = useQuery({
    queryKey: ["store-settings", orgId],
    queryFn: () => fetchSettings(orgId!),
    enabled: Boolean(orgId),
  });

  const original = useMemo(
    () => (consulta.data ? desdeAjustes(consulta.data) : null),
    // `desdeAjustes` se define en el cuerpo de cada pantalla y cambia de
    // identidad en cada render: incluirlo aqui recalcularia siempre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [consulta.data],
  );

  const [borrador, setBorrador] = useState<T | null>(null);
  const [guardando, setGuardando] = useState(false);

  // El borrador se inicializa una vez. Si se resincronizara en cada refetch,
  // una recarga en segundo plano borraria lo que se esta escribiendo.
  useEffect(() => {
    if (original && !borrador) setBorrador(original);
  }, [original, borrador]);

  const sucio = useMemo(() => {
    if (!original || !borrador) return false;
    return JSON.stringify(original) !== JSON.stringify(borrador);
  }, [original, borrador]);

  const set = <K extends keyof T>(campo: K, valor: T[K]) =>
    setBorrador((b) => (b ? { ...b, [campo]: valor } : b));

  async function guardar(cambios: Partial<StoreSettings>) {
    if (!orgId) return false;

    setGuardando(true);
    try {
      const fresco = await updateSettings(orgId, cambios);
      qc.setQueryData(["store-settings", orgId], fresco);
      setBorrador(desdeAjustes(fresco));
      toast.ok("Cambios guardados.");
      return true;
    } catch (error) {
      toast.error(friendlyError(error));
      return false;
    } finally {
      setGuardando(false);
    }
  }

  return {
    consulta,
    borrador,
    set,
    sucio,
    guardando,
    guardar,
    descartar: () => setBorrador(original),
  };
}
